// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import dayjs from 'dayjs';

import { GetInvoiceDetailsChargesQuery } from '../queries/getInvoiceDetailsCharges.query.generated';

import { useCustomerConfig } from '@store';
import { clampNegNum, getMonetaryValue } from '@services/formatService';
import { groupChargesByMove, ChargesByMoveGroup, formatAddressShort } from '@features/invoices/services/calcServiceCharges';

import { css } from '@emotion/css';
import { Button, Icon, Paper, TablePagination, Theme, Tooltip, Typography, useTheme } from '@mui/material';
import { BiSolidAdjust, BiSolidBadgeDollar, BiSolidCheckCircle } from 'react-icons/bi';

import InvoiceDetailsTableBodyCharges from '@features/invoices/components/InvoiceDetailsTableBodyCharges';
import { MOVE_TABLE_COLUMN, type MoveTableColumn, type MoveTableHeader } from '@features/invoices/components/moveTableColumns';
import { InvoiceMoveTable } from '@hopdrive/feature-invoice-display';
import { settledAllocationTotal } from '@hopdrive/sdk-ar';
import { useMoveAmountDues, type InvoiceAmountDue } from '@features/invoices/hooks/useInvoiceAmountDue';

type SortDirection = 'asc' | 'desc';

const defaultOrder: SortDirection = `desc`;
const defaultOrderBy = `MOVE_ID`;

// TYPES ---------------------------------------------------------------- //

type InvoiceDetailsTableChargesProps = {
  invoice?: GetInvoiceDetailsChargesQuery['arinvoices_by_pk'];
  amount?: any;
  archargeIds?: number[];
  handleDisputeModalOpen?: any;
  handlePayNowModalOpen?: any;
  /** Nested in another section, such as a rooftop's expansion. Drops the title and Pay Now, which pays the whole invoice. */
  embedded?: boolean;
};

// COMPONENT ---------------------------------------------------------------- //

const InvoiceDetailsTableCharges = ({
  invoice,
  amount,
  archargeIds,
  handleDisputeModalOpen,
  handlePayNowModalOpen,
  embedded = false,
}: InvoiceDetailsTableChargesProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const clsx = useSxStyles(theme);

  const customerConfig = useCustomerConfig();
  const refLabel = customerConfig?.reference_num_label || 'Reference ID';

  const [order, setOrder] = React.useState<SortDirection>(defaultOrder);
  const [orderBy, setOrderBy] = React.useState(defaultOrderBy);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const payable = amount?.due > 0 && invoice?.status === `closed`;

  const argroups = invoice?.argroups || [];
  const moveGroups = groupChargesByMove(argroups);

  // Each row's paid mark reads sdk-ar's Amount Due for the row, the answer a
  // payment on its charges collects, asked for every row at once.
  const amountDues = useMoveAmountDues(moveGroups);

  // Check if invoice contains any move-type charges (not guestrides)
  const hasMoveCharges = moveGroups.some((group: ChargesByMoveGroup) => group.sourceKind === 'move');

  // Check if any row has a distance (guestrides have none) — used to hide the Distance column entirely
  const hasDistance = moveGroups.some((group: ChargesByMoveGroup) => Number(group.move?.lane?.distance_miles) > 0);

  const handleChangeSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Handle page state
  const handleChangePage = (_event: any, newPage: number) => {
    setPage(newPage);
  };

  // Handle pagination rows state
  const handleChangeRowsPerPage = (event: any) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  // Check if invoice contains any guestride charges
  const hasGuestrides = moveGroups.some((group: ChargesByMoveGroup) => group.sourceKind === 'guestride');

  // Set headers array used in the table
  const buildHeaders = (hiddenColumns: ReadonlySet<MoveTableColumn>): MoveTableHeader[] => [
    { id: `MOVE_ID`, align: `left`, label: `HopDrive\xa0ID` },
    { id: `REF_NUM`, align: `left`, label: refLabel },
    { id: `MOVE_DATE`, align: `left`, label: `Date` },
    { id: `GUEST_NAME`, align: `left`, label: `Guest\xa0Name`, hide: !hasGuestrides },
    { id: `LANE`, align: `left`, label: `Lane`, hide: hiddenColumns.has(MOVE_TABLE_COLUMN.LANE) },
    { id: `DLV_REP`, align: `left`, label: `Rep`, hide: invoice?.customer?.id !== 2 },
    { id: `DISTANCE`, align: `left`, label: `Distance`, hide: !hasDistance || hiddenColumns.has(MOVE_TABLE_COLUMN.DISTANCE) },
    {
      id: `WEIGHT_CLASS`,
      align: `left`,
      label: `Vehicle\xa0Weight\xa0Class`,
      hide: !hasMoveCharges || hiddenColumns.has(MOVE_TABLE_COLUMN.WEIGHT_CLASS),
    },
    { id: `COST`, align: `right`, label: `Cost` },
  ];

  // Build row object for each move group
  const buildRowObject = (moveGroup: ChargesByMoveGroup, amountDue: InvoiceAmountDue) => {
    const move = moveGroup.move;
    let row: any = {};

    row.moveGroup = moveGroup;

    if (moveGroup.sourceKind === 'guestride') {
      const guestride = moveGroup.guestride;
      row.MOVE_ID = guestride?.id ? `R-#${guestride.id}` : null;
      row.REF_NUM = guestride?.reference_num || null;
      row.GUEST_NAME = guestride?.guest_name || null;
      // Guestrides have no lane — format pickup/dropoff with street and city only
      const pickupShort = formatAddressShort(guestride?.pickup_address);
      const dropoffShort = formatAddressShort(guestride?.dropoff_address);
      row.LANE = pickupShort && dropoffShort ? `${pickupShort} to ${dropoffShort}` : pickupShort || dropoffShort || null;
      row.DISTANCE = `—`;

      // For guestrides, get billable_datetime from the first charge
      const firstCharge = moveGroup.charges[0];
      row.MOVE_DATE = firstCharge?.billable_datetime
        ? dayjs(firstCharge.billable_datetime).utc().format(`MM/DD/YYYY`)
        : null;
    } else {
      row.MOVE_ID = move?.id ? `M-#${move.id}` : null;
      row.REF_NUM = move?.reference_num || null;
      row.GUEST_NAME = null; // Moves don't have guest names

      if (move?.lane) {
        row.LANE = move.lane.description || null;
        row.DISTANCE = move.lane.distance_miles ? `${move.lane.distance_miles} mi` : `0 mi`;
      }

      // Find the primary transport charge to get billable_datetime and weight class
      const transportCharge = moveGroup.charges.find((c: any) => c.type === 'move' || c.name === 'Transport' || c.name?.includes('Transport'));
      row.MOVE_DATE = transportCharge?.billable_datetime
        ? dayjs(transportCharge.billable_datetime).utc().format(`MM/DD/YYYY`)
        : null;

      // Get weight class from transport charge notes field (format: "Class 1, $15.00 flat")
      // Extract just the class name before the comma
      row.WEIGHT_CLASS = transportCharge?.notes ? transportCharge.notes.split(',')[0] : null;
    }

    row.DLV_REP =
      move?.tags && move.tags.includes(`delivery reported`) ? (
        <Icon fontSize='small' style={{ margin: 0 }}>
          backup
        </Icon>
      ) : null;

    // Calculate if there are any credits (discounts)
    const hasCredits = moveGroup.credits.length > 0;
    const creditsTotal = moveGroup.credits.reduce((sum, credit) => sum + Number(credit.amount || 0), 0);

    // Payment status. Paid when sdk-ar says nothing is due on the row's charges.
    // While the answer loads, or when it cannot be read, the row shows no mark.
    const paidAmount = settledAllocationTotal(moveGroup.charges.flatMap((charge) => charge.arpaymentdetails ?? []));
    const isPaid = amountDue.status === 'ready' && amountDue.amount <= 0;
    const isPartiallyPaid = amountDue.status === 'ready' && amountDue.amount > 0 && paidAmount > 0;

    row.COST = (
      <div className={cls.rowFlex}>
        {hasCredits ? (
          <Tooltip placement='top' title={`Record has credits for $${creditsTotal.toFixed(2)}`}>
            <span>
              <BiSolidBadgeDollar className={cls.rowIcon} style={{ color: theme?.palette?.text?.disabled }} />
            </span>
          </Tooltip>
        ) : null}

        {isPaid ? (
          <Tooltip placement='top' title={`Paid - ${getMonetaryValue(paidAmount)}`}>
            <span>
              <BiSolidCheckCircle className={cls.rowIcon} style={{ color: theme?.palette?.success?.main }} />
            </span>
          </Tooltip>
        ) : isPartiallyPaid ? (
          <Tooltip placement='top' title={`Partially Paid - ${getMonetaryValue(paidAmount)}`}>
            <span>
              <BiSolidAdjust className={cls.rowIcon} style={{ color: theme?.palette?.warning?.main }} />
            </span>
          </Tooltip>
        ) : null}

        <Typography sx={clsx.rowText}>{getMonetaryValue(clampNegNum(moveGroup.total))}</Typography>
      </div>
    );

    return row;
  };

  // Set rows array with the move group object attached to each row
  const rows = moveGroups?.map((moveGroup: ChargesByMoveGroup, i: number) => {
    return buildRowObject(moveGroup, amountDues[i]);
  });

  return (
    <div className={cls.table}>
      <Paper variant={embedded ? 'elevation' : 'custom'} elevation={0}>
        {!embedded && (
          <>
            <div style={{ padding: `16px` }}>
              <Typography variant='h5' sx={{ lineHeight: 1, fontWeight: 600 }}>
                Charges
              </Typography>
            </div>

            <Button
              sx={clsx.payBtn}
              disabled={!payable}
              color='primary'
              onClick={() => handlePayNowModalOpen({ invoice, ...amount, archargeIds })}
            >
              Pay Now
            </Button>
          </>
        )}

        <InvoiceMoveTable
          headers={buildHeaders}
          order={order}
          orderBy={orderBy}
          onSort={handleChangeSort}
          ariaLabel='invoice-table'
        >
          {hiddenColumns => (
            <InvoiceDetailsTableBodyCharges
              rows={rows}
              order={order}
              orderBy={orderBy}
              page={page}
              rowsPerPage={rowsPerPage}
              handleDisputeModalOpen={handleDisputeModalOpen}
              invoice={invoice}
              hasMoveCharges={hasMoveCharges}
              hasDistance={hasDistance}
              hasGuestrides={hasGuestrides}
              hiddenColumns={hiddenColumns}
            />
          )}
        </InvoiceMoveTable>

        <div>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component='div'
            count={rows?.length || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      </Paper>
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (_theme?: Theme) => ({
  table: css`
    position: relative;
  `,

  rowFlex: css`
    display: flex;
    align-items: center;
  `,
  rowIcon: css`
    display: block;
    margin-right: 4px;
    font-size: 20px;
  `,
});

const useSxStyles = (theme: Theme) => ({
  payBtn: {
    position: 'absolute',
    top: '12px',
    right: '12px',
  },

  rowText: {
    display: 'inline',
    color: 'inherit',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '16px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px',
      lineHeight: '14px',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '10px',
      lineHeight: '12px',
    },
  },
});

// EXPORT ---------------------------------------------------------------- //

export default InvoiceDetailsTableCharges;
